import type { CSSProperties } from 'react'

// §7.5.2 Org-scoped branding: the brand primary resolves from a CSS
// custom property that ThemeProvider writes on mount from the org's
// settings.branding.themeColor. The literal in the fallback matches
// what we shipped before — every uncustomised deployment still renders
// with #0074d4.
export const THEME_PRIMARY_FALLBACK = '#0074d4'
export const THEME_PRIMARY_HOVER_FALLBACK = '#005fa8'

export const colors = {
  // Brand
  primary: `var(--theme-primary, ${THEME_PRIMARY_FALLBACK})`,
  primaryHover: `var(--theme-primary-hover, ${THEME_PRIMARY_HOVER_FALLBACK})`,
  accent: `var(--theme-primary, ${THEME_PRIMARY_FALLBACK})`,

  // Semantic
  danger: '#dc2626',
  success: '#87CB16',
  warning: '#FF9500',

  // Text
  text: '#525f7f',
  textMuted: '#8f96a9',
  textHeading: '#525f7f',

  // Borders
  border: '#d4d4d4',
  borderInput: '#a19fa0',

  // Backgrounds
  bgPage: '#f2f2f2',
  bgCard: '#fff',
  bgMuted: '#f7f7f8',
  bgError: '#fef2f2',
  bgSuccess: '#f0fdf4',
  borderError: '#fecaca',
  borderSuccess: '#bbf7d0',

  // Sidebar
  sidebarBg: '#ffffff',
  sidebarText: '#525f7f',
  sidebarActive: '#f0f4f8',
  sidebarHover: '#f7f8fa',
  sidebarBorder: '#e8ecf0',

  // Table
  tableHeaderBg: '#f9fafb',
  tableRowHover: '#f5f5f5',
  tableRowSelected: '#dbeafe',
  tableBorder: '#e8e8e8',

  // Tabs
  tabActive: '#0074d4',
  tabInactive: '#8f96a9',
  tabBorder: '#e0e0e0',
} as const

export const card: CSSProperties = {
  padding: '1.25rem',
  background: colors.bgCard,
  borderRadius: '8px',
  border: `1px solid ${colors.border}`,
}

export const input: CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: `1px solid ${colors.borderInput}`,
  borderRadius: '3px',
  fontSize: '0.9375rem',
  boxSizing: 'border-box',
  height: '42px',
}

export const label: CSSProperties = {
  display: 'block',
  fontSize: '0.9375rem',
  fontWeight: 500,
  marginBottom: '0.25rem',
  color: colors.textMuted,
}

export const btnBase: CSSProperties = {
  padding: '0.5rem 1rem',
  borderRadius: '0',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.9375rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.75px',
}

export const btnPrimary: CSSProperties = { ...btnBase, background: colors.primary, color: '#fff' }
export const btnSecondary: CSSProperties = { ...btnBase, background: colors.text, color: '#fff' }
export const btnDanger: CSSProperties = { ...btnBase, background: colors.danger, color: '#fff' }

export const tableHeader: CSSProperties = {
  padding: '0.6rem 0.75rem',
  textAlign: 'left',
  fontWeight: 600,
  color: colors.textMuted,
  fontSize: '0.8125rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  borderBottom: `2px solid ${colors.tableBorder}`,
  background: colors.tableHeaderBg,
}

export const tableCell: CSSProperties = {
  padding: '0.5rem 0.75rem',
  fontSize: '0.9375rem',
  borderBottom: `1px solid ${colors.tableBorder}`,
  verticalAlign: 'middle',
}

export const tabItem = (active: boolean): CSSProperties => ({
  padding: '0.5rem 1rem',
  fontSize: '0.9375rem',
  fontWeight: active ? 600 : 400,
  color: active ? colors.accent : colors.textMuted,
  borderBottom: active ? `2px solid ${colors.accent}` : '2px solid transparent',
  background: 'none',
  border: 'none',
  borderBottomStyle: 'solid',
  borderBottomWidth: '2px',
  borderBottomColor: active ? colors.accent : 'transparent',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  textDecoration: 'none',
})
